import { isAddress } from "viem";

/**
 * Inputs for deciding whether to render the "Use this address anyway" button
 * in the token picker. The button lets a user commit a pasted address that
 * couldn't be verified as a known token on the picker's active chain(s).
 */
export interface UnverifiedCommitButtonInputs {
  /** Current value of the search input. */
  searchTerm: string;
  /** True while any chain-scoped address lookup for this search term is in flight. */
  isLoadingLookup: boolean;
  /** Lowercased addresses currently rendered as clickable rows in the dropdown. */
  foundAddresses: string[];
}

/**
 * Returns true when the picker should render a "Use it anyway" escape hatch:
 *   - The user has typed what looks like a valid 42-char EVM address.
 *   - The lookup has finished (not still resolving).
 *   - No clickable result row covers this address.
 *
 * Every other state hides the button — we don't want users committing garbage,
 * mid-lookup value, or a duplicate of a verified token.
 */
export function shouldShowUnverifiedCommitButton(inputs: UnverifiedCommitButtonInputs): boolean {
  const term = inputs.searchTerm.trim();
  if (!term.startsWith("0x") || term.length !== 42) return false;
  // Non-strict: accept any case, including broken EIP-55 checksums. EVM
  // addresses are case-insensitive at the protocol level; the mixed-case
  // checksum is a separate typo-protection layer, not identity. Downstream
  // verification (chain-scoped SimDune lookup) is the real integrity guard.
  if (!isAddress(term, { strict: false })) return false;
  if (inputs.isLoadingLookup) return false;

  const lowered = term.toLowerCase();
  const matches = inputs.foundAddresses.some(addr => addr.toLowerCase() === lowered);
  if (matches) return false;

  return true;
}
